import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../../../../views/pages/auth/auth.service';
import { UserService } from '../../../../core/_services';

@Injectable()
export class InterceptService implements HttpInterceptor {
	private isRefreshing = false;
	private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

	constructor(private router: Router, private authService: AuthService, private userService: UserService) { }

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		const tenantId = this.getTenantId();
		if (tenantId) {
			request = request.clone({
				setHeaders: {
					'tenant-id': tenantId
				}
			});
		}

		if (!request.url.includes('/auth/login') && !request.url.includes('/token/refresh/') && !request.url.includes('get-bureau-data') && !request.url.includes('get-business-detail')) {
			request = this.addToken(request, this.authService.token);
		}

		return next.handle(request).pipe(
			catchError(error => {
				if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
					return this.handleRefreshedToken(request, next);
				}
				return throwError(error);
			})
		);
	}

	private addToken(request: HttpRequest<any>, token: string) {
		if (token) {
			if (request.headers.has('Authorization') || request.headers.has('Token')) {
				return request;
			}
			return request.clone({
				setHeaders: {
					Token: `Bearer ${token}`
				}
			});
		}
		return request;
	}

	private handleRefreshedToken(request: HttpRequest<any>, next: HttpHandler) {
		if (!this.isRefreshing) {
			this.isRefreshing = true;
			this.refreshTokenSubject.next(null);

			const refreshToken = localStorage.getItem('refreshToken');
			if (!refreshToken) {
				this.isRefreshing = false;
				this.router.navigate(['/auth/login']);
				return throwError('No refresh token available');
			}

			return this.userService.getTokens(refreshToken).pipe(
				switchMap((data: any) => {
					this.isRefreshing = false;
					if (data && data.access) {
						this.authService.setTokens({ IdToken: data.access, RefreshToken: data.refresh || refreshToken });
						localStorage.setItem('accessToken', data.access);
						if (data.refresh) {
							localStorage.setItem('refreshToken', data.refresh);
						}
						this.refreshTokenSubject.next(data.access);
						return next.handle(this.addToken(request, data.access));
					} else {
						localStorage.clear();
						this.router.navigate(['/auth/login']);
						return throwError('Invalid tokens returned on refresh');
					}
				}),
				catchError((err) => {
					this.isRefreshing = false;
					localStorage.clear();
					this.router.navigate(['/auth/login']);
					return throwError(err);
				})
			);
		} else {
			return this.refreshTokenSubject.pipe(
				filter(token => token != null),
				take(1),
				switchMap(jwt => {
					return next.handle(this.addToken(request, jwt));
				})
			);
		}
	}

	private getTenantId(): string | null {
		try {
			const userStr = localStorage.getItem('user');
			if (userStr) {
				const user = JSON.parse(userStr);
				if (user && user.org) {
					return user.org;
				}
			}
		} catch (e) { }
		return null;
	}
}
